import React, { useMemo, useState } from "react";
import { supabase } from "./supabaseClient";

/**
 * Feedback.jsx
 * Drop-in beta feedback form for TOFU/EMMA.
 *
 * Usage:
 * <Feedback
 *   appName="TOFU"
 *   sessionId={sessionId}
 *   userId={userId}
 *   onSubmitted={() => navigate("/thanks")}
 * />
 */

export default function Feedback({
  appName = "TOFU",
  sessionId = "",
  userId = "",
  onSubmitted,
}) {
  const initial = useMemo(
    () => ({
      // Context
      sessionId: sessionId || "",
      userId: userId || "",
      childAge: "", // optional: "6 months", "2 years"
      scenarioType: "", // fever, vomiting, milestone, etc.

      // Core outcomes
      calmer: "", // yes/no
      knowsNextStep: "", // yes/no
      confidence: 3, // 1-5
      explicitDecision: "", // yes/no/not_sure

      // Deflection / actions
      searchedElsewhere: "", // no/yes
      searchedWhere: [], // google/chatgpt/friends/doctor/other
      searchedOtherText: "",
      actionTaken: "", // monitor/clinic/er/other
      actionOtherText: "",
      clinicianMatch: "", // mostly/partly/no/didnt_see

      // Quality / safety
      length: "", // too_short/just_right/too_long
      tone: "", // alarmist/balanced/dismissive
      unsafeOrOutdated: "", // yes/no
      unsafeDetails: "",

      // Trust / retention
      useAgain: "", // yes/no
      preferOverChatGPT: "", // tofu/chatgpt/depends
      trustImprovers: [], // pd_reviewed, clearer_thresholds, shorter, continuity, better_sources, ui
      trustOtherText: "",

      // Vision fit
      bestFit: "", // nighttime/triage/milestone/handover/general
      neverBecome: "",

      // Open feedback
      bestPart: "",
      biggestIssue: "",
      oneImprovement: "",

      // Consent
      consentContact: false,
      contactEmail: "",
    }),
    [sessionId, userId]
  );

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const toggleInArray = (key, value) => {
    setForm((p) => {
      const arr = new Set(p[key] || []);
      if (arr.has(value)) arr.delete(value);
      else arr.add(value);
      return { ...p, [key]: Array.from(arr) };
    });
  };

  const validate = () => {
    // Minimal required fields so friction stays low:
    if (!form.calmer) return "Please answer whether you felt calmer.";
    if (!form.knowsNextStep) return "Please answer whether you knew what to do next.";
    if (!form.explicitDecision) return "Please answer whether TOFU gave a clear Yes/No/Not right now.";
    if (!form.actionTaken) return "Please tell us what you ended up doing.";
    if (!form.useAgain) return "Please tell us if you would use TOFU again.";
    if (form.consentContact && !form.contactEmail.trim())
      return "Please provide an email if you consent to be contacted.";
    return "";
  };

  const handleSubmit = async (e) => {
      e.preventDefault();
      setError("");

      const msg = validate();
      if (msg) {
          setError(msg);
          return;
      }

      setSubmitting(true);

      try {
          const payload = {
              app_name: appName,
              session_id: form.sessionId || null,
              user_id: form.userId || null,
              child_age: form.childAge || null,
              scenario_type: form.scenarioType || null,

              calmer: form.calmer,
              knows_next_step: form.knowsNextStep,
              confidence: form.confidence,
              explicit_decision: form.explicitDecision,

              searched_elsewhere: form.searchedElsewhere || null,
              searched_where: form.searchedWhere?.length ? form.searchedWhere : null,
              searched_other_text: form.searchedOtherText || null,

              action_taken: form.actionTaken,
              action_other_text: form.actionOtherText || null,
              clinician_match: form.clinicianMatch || null,

              response_length: form.length || null,
              tone: form.tone || null,
              unsafe_or_outdated: form.unsafeOrOutdated || null,
              unsafe_details: form.unsafeDetails || null,

              use_again: form.useAgain,
              prefer_over_chatgpt: form.preferOverChatGPT || null,
              trust_improvers: form.trustImprovers?.length ? form.trustImprovers : null,
              trust_other_text: form.trustOtherText || null,

              best_fit: form.bestFit || null,
              never_become: form.neverBecome || null,

              best_part: form.bestPart || null,
              biggest_issue: form.biggestIssue || null,
              one_improvement: form.oneImprovement || null,

              consent_contact: !!form.consentContact,
              contact_email: form.consentContact ? (form.contactEmail || null) : null,

              user_agent: navigator.userAgent,
              page_url: window.location.href,
          };

      const { error } = await supabase.from("beta_feedback").insert([payload]);

      if (error) throw error;

      setSubmitted(true);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
    
  if (submitted) {
    return (
      <div style={styles.container}>
        <h2 style={styles.h2}>Thank you</h2>
        <p style={styles.p}>
          Your feedback helps {appName} become calmer, clearer, and more consistent for parents.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.h2}>{appName} Beta Feedback</h2>
      <p style={styles.p}>
        This takes ~2 minutes. Answer based on your most recent interaction. Please do not include
        sensitive personal identifiers.
      </p>

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Context */}
        <Section title="Context (optional)">
          <div style={styles.row}>
            <Field label="Child age (e.g., 6 months, 2 years)">
              <input
                style={styles.input}
                value={form.childAge}
                onChange={(e) => update("childAge", e.target.value)}
                placeholder="Optional"
              />
            </Field>

            <Field label="Topic">
              <select
                style={styles.select}
                value={form.scenarioType}
                onChange={(e) => update("scenarioType", e.target.value)}
              >
                <option value="">Optional</option>
                <option value="fever">Fever</option>
                <option value="vomiting">Vomiting</option>
                <option value="cough">Cough / breathing</option>
                <option value="rash">Rash</option>
                <option value="milestone">Milestones</option>
                <option value="feeding">Feeding</option>
                <option value="sleep">Sleep</option>
                <option value="other">Other</option>
              </select>
            </Field>
          </div>
        </Section>

        {/* Core outcomes */}
        <Section title="Did TOFU reduce anxiety and close the decision?">
          <RadioRow
            label="Did you feel calmer after reading TOFU’s response?"
            name="calmer"
            value={form.calmer}
            onChange={(v) => update("calmer", v)}
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
          />

          <RadioRow
            label="Did you know what to do next?"
            name="knowsNextStep"
            value={form.knowsNextStep}
            onChange={(v) => update("knowsNextStep", v)}
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
          />

          <Field label="How confident did you feel about the next step? (1–5)">
            <input
              type="range"
              min="1"
              max="5"
              value={form.confidence}
              onChange={(e) => update("confidence", Number(e.target.value))}
              style={styles.range}
            />
            <div style={styles.rangeLabels}>
              <span>1</span>
              <span style={{ fontWeight: 600 }}>{form.confidence}</span>
              <span>5</span>
            </div>
          </Field>

          <RadioRow
            label="Did TOFU give a clear decision (Yes / No / Not right now)?"
            name="explicitDecision"
            value={form.explicitDecision}
            onChange={(v) => update("explicitDecision", v)}
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
              { value: "not_sure", label: "Not sure" },
            ]}
          />
        </Section>

        {/* Deflection / behavior */}
        <Section title="After TOFU, what did you do?">
          <RadioRow
            label="Did you still search elsewhere after TOFU?"
            name="searchedElsewhere"
            value={form.searchedElsewhere}
            onChange={(v) => update("searchedElsewhere", v)}
            options={[
              { value: "no", label: "No" },
              { value: "yes", label: "Yes" },
            ]}
          />

          {form.searchedElsewhere === "yes" && (
            <>
              <Field label="Where did you search? (select all that apply)">
                <div style={styles.checkGrid}>
                  <Checkbox
                    label="Google"
                    checked={form.searchedWhere.includes("google")}
                    onChange={() => toggleInArray("searchedWhere", "google")}
                  />
                  <Checkbox
                    label="ChatGPT"
                    checked={form.searchedWhere.includes("chatgpt")}
                    onChange={() => toggleInArray("searchedWhere", "chatgpt")}
                  />
                  <Checkbox
                    label="Friends / family"
                    checked={form.searchedWhere.includes("friends")}
                    onChange={() => toggleInArray("searchedWhere", "friends")}
                  />
                  <Checkbox
                    label="Doctor / clinic"
                    checked={form.searchedWhere.includes("doctor")}
                    onChange={() => toggleInArray("searchedWhere", "doctor")}
                  />
                  <Checkbox
                    label="Other"
                    checked={form.searchedWhere.includes("other")}
                    onChange={() => toggleInArray("searchedWhere", "other")}
                  />
                </div>
              </Field>

              {form.searchedWhere.includes("other") && (
                <Field label="Other (optional)">
                  <input
                    style={styles.input}
                    value={form.searchedOtherText}
                    onChange={(e) => update("searchedOtherText", e.target.value)}
                    placeholder="Where?"
                  />
                </Field>
              )}
            </>
          )}

          <RadioRow
            label="What did you end up doing?"
            name="actionTaken"
            value={form.actionTaken}
            onChange={(v) => update("actionTaken", v)}
            options={[
              { value: "monitor", label: "Stayed home + monitored" },
              { value: "clinic", label: "Clinic / GP same day" },
              { value: "er", label: "ER / urgent care" },
              { value: "other", label: "Other" },
            ]}
          />

          {form.actionTaken === "other" && (
            <Field label="Other (optional)">
              <input
                style={styles.input}
                value={form.actionOtherText}
                onChange={(e) => update("actionOtherText", e.target.value)}
                placeholder="What did you do?"
              />
            </Field>
          )}

          <Field label="If you saw a clinician, did TOFU’s guidance match what you were told?">
            <select
              style={styles.select}
              value={form.clinicianMatch}
              onChange={(e) => update("clinicianMatch", e.target.value)}
            >
              <option value="">Select</option>
              <option value="mostly">Mostly</option>
              <option value="partly">Partly</option>
              <option value="no">No</option>
              <option value="didnt_see">Did not see a clinician</option>
            </select>
          </Field>
        </Section>

        {/* Quality / safety */}
        <Section title="Quality & safety">
          <Field label="Response length">
            <select
              style={styles.select}
              value={form.length}
              onChange={(e) => update("length", e.target.value)}
            >
              <option value="">Select</option>
              <option value="too_short">Too short</option>
              <option value="just_right">Just right</option>
              <option value="too_long">Too long</option>
            </select>
          </Field>

          <Field label="Tone">
            <select
              style={styles.select}
              value={form.tone}
              onChange={(e) => update("tone", e.target.value)}
            >
              <option value="">Select</option>
              <option value="balanced">Balanced</option>
              <option value="alarmist">Too alarmist</option>
              <option value="dismissive">Too dismissive</option>
            </select>
          </Field>

          <RadioRow
            label="Did you notice anything unsafe or outdated?"
            name="unsafeOrOutdated"
            value={form.unsafeOrOutdated}
            onChange={(v) => update("unsafeOrOutdated", v)}
            options={[
              { value: "no", label: "No" },
              { value: "yes", label: "Yes" },
            ]}
          />

          {form.unsafeOrOutdated === "yes" && (
            <Field label="Tell us what felt unsafe/outdated (optional)">
              <textarea
                style={styles.textarea}
                value={form.unsafeDetails}
                onChange={(e) => update("unsafeDetails", e.target.value)}
                placeholder="Example: advice that felt wrong, outdated, or too risky"
              />
            </Field>
          )}
        </Section>

        {/* Trust & retention */}
        <Section title="Trust & repeat use">
          <RadioRow
            label={`Would you use ${appName} again in a similar situation?`}
            name="useAgain"
            value={form.useAgain}
            onChange={(v) => update("useAgain", v)}
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
          />

          <Field label={`For this type of question, do you prefer ${appName} or ChatGPT?`}>
            <select
              style={styles.select}
              value={form.preferOverChatGPT}
              onChange={(e) => update("preferOverChatGPT", e.target.value)}
            >
              <option value="">Select</option>
              <option value="tofu">{appName}</option>
              <option value="chatgpt">ChatGPT</option>
              <option value="depends">Depends</option>
            </select>
          </Field>

          <Field label="What would make you trust TOFU more? (select all)">
            <div style={styles.checkGrid}>
              <Checkbox
                label="Pediatrician-reviewed responses"
                checked={form.trustImprovers.includes("pd_reviewed")}
                onChange={() => toggleInArray("trustImprovers", "pd_reviewed")}
              />
              <Checkbox
                label="Clearer thresholds / escalation rules"
                checked={form.trustImprovers.includes("clear_thresholds")}
                onChange={() => toggleInArray("trustImprovers", "clear_thresholds")}
              />
              <Checkbox
                label="Shorter, more direct answers"
                checked={form.trustImprovers.includes("shorter")}
                onChange={() => toggleInArray("trustImprovers", "shorter")}
              />
              <Checkbox
                label="Better memory/continuity"
                checked={form.trustImprovers.includes("continuity")}
                onChange={() => toggleInArray("trustImprovers", "continuity")}
              />
              <Checkbox
                label="Better sources / citations"
                checked={form.trustImprovers.includes("better_sources")}
                onChange={() => toggleInArray("trustImprovers", "better_sources")}
              />
              <Checkbox
                label="Better UI / speed"
                checked={form.trustImprovers.includes("ui")}
                onChange={() => toggleInArray("trustImprovers", "ui")}
              />
              <Checkbox
                label="Other"
                checked={form.trustImprovers.includes("other")}
                onChange={() => toggleInArray("trustImprovers", "other")}
              />
            </div>
          </Field>

          {form.trustImprovers.includes("other") && (
            <Field label="Other (optional)">
              <input
                style={styles.input}
                value={form.trustOtherText}
                onChange={(e) => update("trustOtherText", e.target.value)}
                placeholder="What would increase trust?"
              />
            </Field>
          )}
        </Section>

        {/* Vision fit */}
        <Section title="Vision fit">
          <Field label={`Where does ${appName} fit best for you?`}>
            <select
              style={styles.select}
              value={form.bestFit}
              onChange={(e) => update("bestFit", e.target.value)}
            >
              <option value="">Select</option>
              <option value="nighttime">Night-time uncertainty</option>
              <option value="triage">Quick triage (what can wait vs act)</option>
              <option value="milestone">Milestone reassurance</option>
              <option value="handover">Preparing a summary for a doctor</option>
              <option value="general">General parenting questions</option>
            </select>
          </Field>

          <Field label="One thing TOFU should NEVER become (optional)">
            <input
              style={styles.input}
              value={form.neverBecome}
              onChange={(e) => update("neverBecome", e.target.value)}
              placeholder="Example: ‘a chatty explainer’ or ‘diagnosing tool’"
            />
          </Field>
        </Section>

        {/* Open feedback */}
        <Section title="Open feedback (optional)">
          <Field label="What was the best part of the experience?">
            <textarea
              style={styles.textarea}
              value={form.bestPart}
              onChange={(e) => update("bestPart", e.target.value)}
              placeholder="Optional"
            />
          </Field>

          <Field label="What was the biggest issue?">
            <textarea
              style={styles.textarea}
              value={form.biggestIssue}
              onChange={(e) => update("biggestIssue", e.target.value)}
              placeholder="Optional"
            />
          </Field>

          <Field label="If you could change one thing, what should it be?">
            <textarea
              style={styles.textarea}
              value={form.oneImprovement}
              onChange={(e) => update("oneImprovement", e.target.value)}
              placeholder="Optional"
            />
          </Field>
        </Section>

        {/* Consent */}
        <Section title="Follow-up (optional)">
          <Checkbox
            label="You may contact me for a quick follow-up about my feedback"
            checked={form.consentContact}
            onChange={(e) => update("consentContact", e.target.checked)}
          />
          {form.consentContact && (
            <Field label="Email">
              <input
                style={styles.input}
                value={form.contactEmail}
                onChange={(e) => update("contactEmail", e.target.value)}
                placeholder="you@example.com"
              />
            </Field>
          )}
        </Section>

        {error && <div style={styles.error}>{error}</div>}

        <button type="submit" style={styles.button} disabled={submitting}>
          {submitting ? "Submitting..." : "Submit feedback"}
        </button>

        {/* Hidden context (in case you want to display for debugging) */}
        <div style={styles.meta}>
          {form.sessionId ? <span>Session: {form.sessionId}</span> : null}
        </div>
      </form>
    </div>
  );
}

/* ---------- Small UI helpers ---------- */

function Section({ title, children }) {
  return (
    <div style={styles.section}>
      <h3 style={styles.h3}>{title}</h3>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={styles.field}>
      <div style={styles.label}>{label}</div>
      {children}
    </label>
  );
}

function RadioRow({ label, name, value, onChange, options }) {
  return (
    <div style={styles.radioRow}>
      <div style={styles.label}>{label}</div>
      <div style={styles.radioOptions}>
        {options.map((opt) => (
          <label key={opt.value} style={styles.radioOption}>
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
            />
            <span style={{ marginLeft: 8 }}>{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label style={styles.checkbox}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span style={{ marginLeft: 8 }}>{label}</span>
    </label>
  );
}

/* ---------- Minimal inline styles ---------- */

const styles = {
  container: {
    maxWidth: 760,
    margin: "24px auto",
    padding: "0 16px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  h2: { margin: "8px 0 12px", fontSize: 26 },
  h3: { margin: "0 0 12px", fontSize: 18 },
  p: { margin: "0 0 16px", opacity: 0.85, lineHeight: 1.5 },
  form: { display: "flex", flexDirection: "column", gap: 14 },
  section: {
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 12,
    padding: 14,
  },
  sectionBody: { display: "flex", flexDirection: "column", gap: 12 },
  field: { display: "flex", flexDirection: "column", gap: 8 },
  label: { fontSize: 14, fontWeight: 600 },
  row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  input: {
    border: "1px solid rgba(0,0,0,0.15)",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 14,
    outline: "none",
  },
  textarea: {
    border: "1px solid rgba(0,0,0,0.15)",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 14,
    minHeight: 90,
    resize: "vertical",
    outline: "none",
  },
  select: {
    border: "1px solid rgba(0,0,0,0.15)",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 14,
    outline: "none",
    background: "white",
  },
  radioRow: { display: "flex", flexDirection: "column", gap: 8 },
  radioOptions: { display: "flex", flexWrap: "wrap", gap: 12 },
  radioOption: {
    display: "flex",
    alignItems: "center",
    border: "1px solid rgba(0,0,0,0.08)",
    padding: "8px 10px",
    borderRadius: 10,
  },
  checkbox: { display: "flex", alignItems: "center" },
  checkGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginTop: 6,
  },
  range: { width: "100%" },
  rangeLabels: { display: "flex", justifyContent: "space-between", fontSize: 13, opacity: 0.9 },
  button: {
    border: "none",
    borderRadius: 12,
    padding: "12px 14px",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  error: {
    padding: 12,
    borderRadius: 12,
    background: "rgba(255,0,0,0.08)",
    border: "1px solid rgba(255,0,0,0.2)",
    fontSize: 14,
  },
  meta: { marginTop: 8, fontSize: 12, opacity: 0.6 },
};
